import type { Font } from 'opentype.js';

/**
 * Работа с текстом через метрики конкретного файла шрифта.
 *
 * Тот же самый шрифт используется и для предпросмотра, и для экспорта:
 * подпись всегда превращается в контуры, поэтому результат не зависит от
 * того, какие шрифты установлены у получателя файла.
 */

export type MeasuredLine = {
  text: string;
  width: number;
};

export type TextBlockMetrics = {
  lines: MeasuredLine[];
  /** Ширина самой длинной строки. */
  width: number;
  /** Полная высота блока с учётом межстрочного расстояния. */
  height: number;
  lineHeight: number;
  ascender: number;
};

export const LINE_HEIGHT_FACTOR = 1.25;

export function advanceWidth(font: Font, text: string, size: number): number {
  if (text === '') return 0;
  return font.getAdvanceWidth(text, size);
}

/** Символы, для которых в шрифте нет глифа (получают .notdef). */
export function missingGlyphs(font: Font, text: string): string[] {
  const missing = new Set<string>();
  for (const ch of text) {
    if (ch === '\n' || ch === '\r') continue;
    const glyph = font.charToGlyph(ch);
    if (!glyph || glyph.index === 0) missing.add(ch);
  }
  return [...missing];
}

/**
 * Переносит текст по словам под заданную ширину.
 * Явные переводы строк сохраняются. Слово, которое не помещается целиком,
 * разрывается по символам — молча обрезать текст нельзя.
 */
export function wrapText(font: Font, text: string, size: number, maxWidth: number): string[] {
  const out: string[] = [];
  const paragraphs = text.split(/\r\n|\r|\n/);

  for (const paragraph of paragraphs) {
    if (paragraph.trim() === '') {
      out.push('');
      continue;
    }
    const words = paragraph.split(/(\s+)/).filter((w) => w !== '');
    let line = '';
    for (const word of words) {
      const candidate = line + word;
      if (line !== '' && advanceWidth(font, candidate.trimEnd(), size) > maxWidth) {
        out.push(line.trimEnd());
        line = word.trimStart();
        // Одно слово шире строки — режем по символам.
        while (advanceWidth(font, line, size) > maxWidth && line.length > 1) {
          let cut = line.length - 1;
          while (cut > 1 && advanceWidth(font, line.slice(0, cut), size) > maxWidth) cut -= 1;
          out.push(line.slice(0, cut));
          line = line.slice(cut);
        }
      } else {
        line = candidate;
      }
    }
    out.push(line.trimEnd());
  }
  return out;
}

export function measureBlock(
  font: Font,
  text: string,
  size: number,
  maxWidth: number,
): TextBlockMetrics {
  const lines = wrapText(font, text, size, maxWidth).map((t) => ({
    text: t,
    width: advanceWidth(font, t, size),
  }));
  const lineHeight = size * LINE_HEIGHT_FACTOR;
  const unitsPerEm = font.unitsPerEm || 1000;
  const ascender = (font.ascender / unitsPerEm) * size;
  return {
    lines,
    width: lines.reduce((m, l) => Math.max(m, l.width), 0),
    height: lines.length * lineHeight,
    lineHeight,
    ascender,
  };
}

/**
 * Контуры строки текста. x/y — левая точка базовой линии.
 * Возвращается строка path data; никакого <text> в экспорте нет.
 */
export function lineToPathData(
  font: Font,
  text: string,
  x: number,
  y: number,
  size: number,
  decimals = 2,
): string {
  if (text === '') return '';
  return font.getPath(text, x, y, size).toPathData(decimals);
}
